import { Loader2 } from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';

import {
  findActiveKaraokeLine,
  parseAssKaraoke,
  type KaraokeLine,
} from '@/lib/karaokes';
import { playerController } from '@/lib/music/player-controller';
import {
  useMusicPlayerStore,
  type MiniPlayerTrack,
} from '@/lib/music-player-store';

export type LyricsScale = 'compact' | 'immersive';

interface KaraokeLyricsProps {
  track: MiniPlayerTrack;
  /**
   * `compact` is sized for the right-side drawer (text-sm/lg). `immersive`
   * is sized for the full-screen overlay (text-2xl/4xl with extra
   * breathing room).
   */
  scale?: LyricsScale;
}

type Status = 'loading' | 'ready' | 'empty' | 'error';

/**
 * Spotify-style synced lyrics. The active line is large and bright;
 * past lines dim out, future lines are mid-tone. Per-syllable fill
 * animation uses a two-color background-clip text gradient so the
 * unfilled portion stays in the muted-foreground tone instead of going
 * transparent (the previous bug).
 *
 * Click any line to seek. Auto-scrolls the active line into view.
 */
export function KaraokeLyrics({
  track,
  scale = 'compact',
}: KaraokeLyricsProps): ReactElement {
  const [status, setStatus] = useState<Status>('loading');
  const [lines, setLines] = useState<KaraokeLine[]>([]);
  const currentTime = useMusicPlayerStore((s) => s.currentTime);
  const currentMs = Math.round(currentTime * 1000);

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    setLines([]);

    const filename = track.lyricsUrl
      ? track.lyricsUrl.split('/').pop()?.split('?')[0] ?? ''
      : '';
    if (!filename) {
      setStatus('empty');
      return () => controller.abort();
    }

    fetch(`/api/karaokes/lyrics?file=${encodeURIComponent(filename)}`, {
      signal: controller.signal,
    })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error('http'))))
      .then((text) => {
        if (controller.signal.aborted) return;
        const parsed = parseAssKaraoke(text);
        setLines(parsed);
        setStatus(parsed.length === 0 ? 'empty' : 'ready');
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStatus('error');
      });

    return () => controller.abort();
  }, [track.id, track.lyricsUrl]);

  const activeIndex = useMemo(
    () => findActiveKaraokeLine(lines, currentMs),
    [lines, currentMs]
  );

  // Auto-scroll the active line to the centre of the viewport. Smooth
  // behaviour gives the Spotify-style "lyrics flow" feel.
  const activeRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeIndex]);

  if (status === 'loading') {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading lyrics…
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        Could not load lyrics.
      </div>
    );
  }
  if (status === 'empty') {
    return (
      <div className="rounded-xl border border-border/60 bg-background/40 p-4 text-center text-sm text-muted-foreground">
        No lyrics available for this kara.
      </div>
    );
  }

  const isImmersive = scale === 'immersive';
  const sizes = isImmersive
    ? {
        active: 'text-2xl sm:text-3xl md:text-4xl font-bold',
        inactive: 'text-lg sm:text-xl',
        spacing: 'space-y-5 sm:space-y-6',
        padding: 'py-[40vh]',
      }
    : {
        active: 'text-xl font-semibold',
        inactive: 'text-sm',
        spacing: 'space-y-3',
        padding: 'py-12',
      };

  return (
    <ol
      className={`mx-auto max-w-2xl px-2 ${sizes.spacing} ${sizes.padding} ${
        isImmersive ? 'text-center' : 'text-left'
      }`}
    >
      {lines.map((line, i) => (
        <LyricLine
          key={`${line.startMs}-${i}`}
          // eslint-disable-next-line react/no-array-index-key
          line={line}
          currentMs={currentMs}
          state={
            i === activeIndex
              ? 'active'
              : i < activeIndex
                ? 'past'
                : 'future'
          }
          activeRef={i === activeIndex ? activeRef : null}
          sizes={sizes}
          onSeek={() => playerController.seek(line.startMs / 1000)}
        />
      ))}
    </ol>
  );
}

interface LyricLineProps {
  line: KaraokeLine;
  currentMs: number;
  state: 'active' | 'past' | 'future';
  activeRef: React.RefObject<HTMLLIElement | null> | null;
  sizes: { active: string; inactive: string };
  onSeek: () => void;
}

function LyricLine({
  line,
  currentMs,
  state,
  activeRef,
  sizes,
  onSeek,
}: LyricLineProps): ReactElement {
  const isActive = state === 'active';
  const opacity = state === 'past' ? 'opacity-30' : state === 'future' ? 'opacity-65' : '';
  const sizeClass = isActive ? sizes.active : sizes.inactive;
  const lineCurrentMs = currentMs - line.startMs;
  const hasTiming = isActive && line.syllables.some((s) => s.durationMs > 0);

  return (
    <li
      ref={activeRef}
      className={`scroll-mt-12 transition-all duration-300 ease-out ${sizeClass} ${opacity} ${
        isActive ? 'text-foreground' : 'cursor-pointer text-muted-foreground hover:text-foreground'
      }`}
    >
      <button
        type="button"
        onClick={onSeek}
        className="block w-full leading-snug"
      >
        {hasTiming ? (
          <KaraokeText line={line} lineCurrentMs={lineCurrentMs} />
        ) : (
          line.text
        )}
      </button>
    </li>
  );
}

/**
 * Renders the active line with progressive per-syllable fill. The
 * gradient uses two solid HSL colors (primary + muted-foreground) so
 * the unfilled portion stays visible — fixing the all-transparent-text
 * regression the previous `currentColor` version had.
 */
function KaraokeText({
  line,
  lineCurrentMs,
}: {
  line: KaraokeLine;
  lineCurrentMs: number;
}): ReactElement {
  return (
    <span className="leading-snug">
      {line.syllables.map((syl, idx) => {
        const elapsed = lineCurrentMs - syl.startMs;
        const ratio =
          syl.durationMs === 0
            ? elapsed >= 0
              ? 1
              : 0
            : Math.max(0, Math.min(1, elapsed / syl.durationMs));
        const pct = (ratio * 100).toFixed(1);
        const style: CSSProperties = {
          backgroundImage: `linear-gradient(to right, hsl(var(--primary)) ${pct}%, hsl(var(--muted-foreground)) ${pct}%)`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
          // The text appears anti-aliased and crisp despite the
          // background-clip — explicit padding-bottom keeps descenders
          // from being clipped on some browsers.
          paddingBottom: '0.05em',
        };
        return (
          <span
            // eslint-disable-next-line react/no-array-index-key
            key={`syl-${idx}`}
            style={style}
          >
            {syl.text}
          </span>
        );
      })}
    </span>
  );
}

export default KaraokeLyrics;
